import React, { useState, useEffect } from "react";
import { bool, shape, arrayOf } from "prop-types";
import Heading from "pages/_components/Heading";
import { connect } from "react-redux";
import { compose } from "redux";
import { resizableRoute } from "pages/_components/Resizable";
import { Button, Box, BottomSheet, Modal, Card, Text, List } from "@ui-kit/components/index.es";
import FormattedAmount from "pages/_components/FormattedAmount";
import * as i18n from "util/i18n";
import FormattedDate from "pages/_components/FormattedDate";
import { selectors as directDebitSelectors } from "reducers/directDebit";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { selectors as sessionSelectors } from "reducers/session";
import * as configUtils from "util/config";
import moment from "moment";
import * as accountUtils from "util/accounts";
import { useEmbeddedV3ShellLayoutSync } from "util/v3ShellLayoutSync";

const FORM_ID = "directDebit.create.table.header";

const ROW_PER_PAGE = 5;

const NewDirectDebitSummaryData = (props) => {
    const [showDetails, setShowDetails] = useState(false);
    const [accountName, setAccountName] = useState("");
    const [totalPages, setTotalPages] = useState(0);
    const [pageNumber, setPageNumber] = useState(0);
    const [dataDetails, setDataDetails] = useState([]);

    const { transactionData, accounts, loggedUser, dispatch, isDesktop, embeddedInV3Shell } = props;

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell,
        isDesktop,
        titleKey: "directDebit.success.title",
        goBackPath: "/directDebit/transaction",
        scopes: ["directDebit"],
    });

    const handleClose = () => {
        setShowDetails(false);
    };

    useEffect(() => {
        if (accounts.length === 0) {
            dispatch(accountsActions.listAccounts(loggedUser?.userId));
        }
    }, []);

    const getSendDateFormat = (date) => {
        const hours = configUtils.get("time.limit.directDebitRequest.extract", "12");
        const tempDate = date && hours ? moment(date).set({ hour: hours, minute: "00", second: "00" }) : moment(date);
        const processDate = moment(date).isAfter(tempDate) ? tempDate.add(1, "days") : tempDate;
        return processDate.format("DD/MM/YYYY HH:mm");
    };

    useEffect(() => {
        if (transactionData?.data && accounts.length > 0) {
            const account = accounts.find((acc) => acc.cbu === transactionData.data.creditAccount);
            if (account) {
                setAccountName(accountUtils.getAccountName(account));
            }
        }
    }, [transactionData, accounts]);

    useEffect(() => {
        setDataDetails(transactionData?.data?.file?.details || []);
        setPageNumber(1);
        setTotalPages(Math.ceil((transactionData?.data?.file?.details?.length || 0) / ROW_PER_PAGE));
    }, [transactionData?.data?.file?.details]);

    const getLabels = (id) => {
        const labels = {
            type: "Type",
            creditAccount: i18n.get("directDebit.creditAccount.actionConfirmation.label").toUpperCase(),
            fileReference: i18n.get("directDebit.reference.actionConfirmation.label").toUpperCase(),
            uploadDate: i18n.get("directDebit.loadDate.actionConfirmation.label").toUpperCase(),
            chargesOrdersCount: i18n.get("directDebit.debitOrders.actionConfirmation.label").toUpperCase(),
            totalAmount: i18n.get("directDebit.totalAmount.actionConfirmation.label").toUpperCase(),
            cbu: i18n.get("directDebit.cbu.detail.label"),
            dueDate: i18n.get("directDebit.dueDate.detail.label"),
            unequivocalReference: i18n.get("directDebit.unequivocalReference.detail.label"),
            reference: i18n.get("directDebit.reference.detail.label"),
            payroll: i18n.get("directDebit.payrollsNumber.actionConfirmation.label").toUpperCase(),
            fileName: i18n.get("directDebit.fileName.actionConfirmation.label").toUpperCase(),
            sendDate: i18n.get("directDebit.sendDate.actionConfirmation.label").toUpperCase(),
            viewDetails: i18n.get("directDebit.viewDetails.detail.label"),
            details: i18n.get("directDebit.details.detail.label"),
            close: i18n.get("directDebit.close.detail.label"),
            motivation: i18n.get("directDebit.motivation.detail.label"),
        };
        return labels[id] || id;
    };

    const renderList = ({ id, cbu, currency, amount, dueDate, unequivocalReference, reference, motivation }) => (
        <Card
            key={id}
            p={12}
            my={16}
            mt={0}
            title={
                <Text
                    variant={isDesktop ? "bodyBold" : "body2"}
                    css={isDesktop ? {} : { whiteSpace: "normal", wordBreak: "break-word" }}>
                    {!isDesktop
                        ? `${getLabels("dueDate")}: ${moment(dueDate).format("DD/MM/YYYY")}`
                        : `${getLabels("cbu")}: ${cbu}`}
                </Text>
            }
            content={
                isDesktop ? (
                    <Box flex gap={12} align="center">
                        <Text variant="body2">{`${getLabels("cbu")}: ${cbu}`}</Text>
                    </Box>
                ) : (
                    <Box column gap={4} w="100%">
                        <Box justify="between" w="100%">
                            <Text variant="bodyBold" css={{ whiteSpace: "nowrap" }}>
                                {getLabels("dueDate").toUpperCase()}
                            </Text>
                            <Text variant="body2">{moment(dueDate).format("DD/MM/YYYY")}</Text>
                        </Box>
                        <Box justify="between">
                            <Text variant="bodyBold">{getLabels("unequivocalReference").toUpperCase()}</Text>
                            <Text variant="body2">{unequivocalReference}</Text>
                        </Box>
                        <Box justify="between">
                            <Text variant="bodyBold">{getLabels("reference").toUpperCase()}</Text>
                            <Text variant="body2">{reference}</Text>
                        </Box>
                        <Box justify="between">
                            <Text variant="bodyBold">{getLabels("motivation").toUpperCase()}</Text>
                            <Text variant="body2">{motivation}</Text>
                        </Box>
                    </Box>
                )
            }
            infoOne={
                isDesktop ? (
                    <Text
                        variant="body2"
                        css={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {`${getLabels("unequivocalReference")}: `}
                        <Text
                            as="span"
                            variant="body2"
                            css={{
                                maxWidth: 136,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "inline",
                                verticalAlign: "bottom",
                            }}>
                            {unequivocalReference}
                        </Text>
                    </Text>
                ) : (
                    undefined
                )
            }
            infoTwo={
                isDesktop ? (
                    <Text
                        variant="body2"
                        css={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {`${getLabels("reference")}: `}
                        <Text
                            as="span"
                            variant="body2"
                            css={{
                                maxWidth: 175,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "inline",
                                verticalAlign: "bottom",
                            }}>
                            {reference}
                        </Text>
                    </Text>
                ) : (
                    undefined
                )
            }
            bottom={
                isDesktop ? (
                    <Box gap={12} align="center">
                        <Text variant="captionBold">{`${getLabels("motivation")}: ${motivation}`}</Text>
                    </Box>
                ) : (
                    undefined
                )
            }
            currency={currency}
            amount={amount}
        />
    );

    return (
        <>
            {transactionData?.idTransaction && (
                <>
                    <>
                        <Heading.DataGroup
                            containerClassName="transaction-ticket-detail data-wrapper-flex space-between"
                            labelClassName="data-label-medium"
                            dataClassName="data-desc"
                            label={`${FORM_ID}.dateCreate`}
                            data={
                                <FormattedDate
                                    date={transactionData.modificationDateTime}
                                    dateFormat="dd/MM/yyyy"
                                    showTime
                                    timeWithoutSeconds
                                />
                            }
                        />
                        <Heading.DataGroup
                            containerClassName="transaction-ticket-detail data-wrapper-flex space-between"
                            labelClassName="data-label-medium"
                            dataClassName="data-desc"
                            label={`${FORM_ID}.voucher`}
                            data={transactionData?.idTransaction?.substring(0, 8)?.toUpperCase() || ""}
                        />

                        <Heading.DataGroup
                            containerClassName="transaction-ticket-detail data-wrapper-flex space-between"
                            labelClassName="data-label-medium"
                            dataClassName="data-desc"
                            label={`${FORM_ID}.creditAccount`}
                            data={accountName}
                        />
                        <Heading.DataGroup
                            containerClassName="transaction-ticket-detail data-wrapper-flex space-between"
                            labelClassName="data-label-medium"
                            dataClassName="data-desc"
                            label={`${FORM_ID}.fileReference`}
                            data={transactionData?.data?.reference || ""}
                        />
                        <Heading.DataGroup
                            containerClassName="transaction-ticket-detail data-wrapper-flex space-between"
                            labelClassName="data-label-medium"
                            dataClassName="data-desc"
                            label={`${FORM_ID}.payroll`}
                            data={transactionData?.data?.payrollsNumber?.label || ""}
                        />

                        <Heading.DataGroup
                            containerClassName="transaction-ticket-detail data-wrapper-flex space-between"
                            labelClassName="data-label-medium"
                            dataClassName="data-desc"
                            label={`${FORM_ID}.dateUpload`}
                            data={
                                <p className="data-desc m-0">
                                    <FormattedDate
                                        date={transactionData?.creationDateTime}
                                        dateFormat="dd/MM/yyyy HH:mm"
                                        showTime
                                        timeWithoutSeconds
                                    />
                                </p>
                            }
                        />
                        <Heading.DataGroup
                            containerClassName="transaction-ticket-detail data-wrapper-flex space-between"
                            labelClassName="data-label-medium"
                            dataClassName="data-desc"
                            label={`${FORM_ID}.fileName`}
                            data={
                                transactionData?.data?.file?.newFileName?.split(".")[0] ||
                                transactionData?.data?.file?.fileName?.split(".")[0]
                            }
                        />

                        <Heading.DataGroup
                            containerClassName="transaction-ticket-detail data-wrapper-flex space-between"
                            labelClassName="data-label-medium"
                            dataClassName="data-desc"
                            label={`${FORM_ID}.chargesOrdersCount`}
                            data={transactionData.data.file.validLines}
                        />

                        <Heading.DataGroup
                            containerClassName="transaction-ticket-detail data-wrapper-flex space-between"
                            labelClassName="data-label-medium"
                            dataClassName="data-desc"
                            label={`${FORM_ID}.totalAmount`}
                            data={
                                <FormattedAmount
                                    currency={transactionData?.data?.file?.totalAmount?.currency}
                                    quantity={transactionData?.data?.file?.totalAmount?.bigQuantity}
                                    notBold
                                />
                            }
                        />

                        <Heading.DataGroup
                            containerClassName="transaction-ticket-detail data-wrapper-flex space-between"
                            labelClassName="data-label-medium"
                            dataClassName="data-desc"
                            label={`${FORM_ID}.sendDate`}
                            data={getSendDateFormat(transactionData?.creationDateTime)}
                        />

                        <Box flex align="center" justify="center" my={12}>
                            <Button
                                aria-label="Ver detalle"
                                text={i18n.get("directDebit.create.button.viewDetail")}
                                variant="text"
                                size="Large"
                                type="button"
                                onClick={() => setShowDetails(true)}
                            />
                        </Box>
                    </>
                    {!isDesktop ? (
                        <BottomSheet isVisible={showDetails} onClose={handleClose} title={getLabels("details")}>
                            {dataDetails.map(renderList)}
                        </BottomSheet>
                    ) : (
                        <Modal
                            isVisible={showDetails}
                            onClose={handleClose}
                            css={{ width: "80vw !important" }}
                            autoWidth>
                            <Box column gap={16} p={16} w="100%" css={{ boxSizing: "border-box" }}>
                                <Box justify="end" align="center" w="100%">
                                    <Button
                                        aria-label={getLabels("close")}
                                        variant="text"
                                        color="black"
                                        icon="Cancel"
                                        onClick={handleClose}
                                    />
                                </Box>
                                <List
                                    array={dataDetails.slice(
                                        (pageNumber - 1) * ROW_PER_PAGE,
                                        pageNumber * ROW_PER_PAGE,
                                    )}
                                    mapCallback={renderList}
                                    loading={false}
                                    paginationProps={{
                                        totalPages,
                                        pageNumber,
                                        action: (pn) => setPageNumber(pn),
                                    }}
                                />
                            </Box>
                        </Modal>
                    )}
                </>
            )}
        </>
    );
};

NewDirectDebitSummaryData.propTypes = {
    transactionData: shape({}).isRequired,
    accounts: arrayOf(shape({})).isRequired,
    loggedUser: shape({}).isRequired,
    dispatch: Function.prototype,
    isDesktop: bool,
    embeddedInV3Shell: bool,
};

NewDirectDebitSummaryData.defaultProps = {
    dispatch: () => {},
    isDesktop: false,
    embeddedInV3Shell: false,
};

const mapStateToProps = (state) => ({
    transactionData: directDebitSelectors.getDirectDebitTransactionData(state),
    loggedUser: sessionSelectors.getUser(state),
    accounts: accountsSelectors.getAccounts(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(NewDirectDebitSummaryData);
